import {useRef, useState, useEffect, useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {
  ugcReviewDataSelector,
  ugcReviewListSelector,
  ugcSummarySelector,
} from '@stencil/hotels-cg-reducer/src/ugc/selectors';
import {
  countrySelectorFn,
  pokusExpDataSelectorFn,
} from '@stencil/hotels-common-reducer/src/selectors';
import {hotelMMTIdSelector} from '@stencil/hotels-cg-reducer/src/details/selectors';
import {TOTAL_REVIEWS_PER_PAGE} from '@stencil/hotels-utils/src/common-utils';
import {fetchUgcReviews} from '../../hotels-apis/hotels-cg-api/src/ugc/ugc-reviews-api';
import {scrollToEl} from '@stencil/hotels-utils/src/scroll-into-view-details';

const useFetchGiOtaReviewsV2 = ({
  subConcepts = [],
  flavour = 'pwa',
  onExpediaReviewsLoaded,
  scrollId = '',
  sortBy = '',
  isMobile = false,
}) => {
  const isInitializedRef = useRef(false);
  const totalReviewsCount = useRef(0);

  const {nextOTA, giReviewCount, expReviewCount} = useSelector(ugcReviewDataSelector);
  const reviewList = useSelector(ugcReviewListSelector);
  const ugcSummary = useSelector(ugcSummarySelector);
  const countryCode = useSelector(countrySelectorFn);
  const pokusExpData = useSelector(pokusExpDataSelectorFn);
  const hotelId = useSelector(hotelMMTIdSelector) || '';

  const initialOTA = useMemo(
    () => nextOTA || ugcSummary?.availableOTAs?.[0] || ugcSummary?.source || '',
    [nextOTA, ugcSummary],
  );
  const [reviews, setReviews] = useState(reviewList || []);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [nextOta, setNextOta] = useState(initialOTA);
  const [giReviewsCount, setGiReviewsCount] = useState(
    giReviewCount || ugcSummary?.reviewCount || 0,
  );

  const [isExpediaReviewLoaded, setIsExpediaReviewLoaded] = useState(false);

  const page = useMemo(
    () => Math.floor(currentOffset / TOTAL_REVIEWS_PER_PAGE) + 1,
    [currentOffset],
  );

  const fetchReviews = useCallback(
    async ({offset = 0, limit = TOTAL_REVIEWS_PER_PAGE, ota = nextOta}) => {
      if (!ota) return [];
      const requestData = {
        hotelId,
        countryCode,
        availableOTA: ugcSummary?.availableOTAs,
        travelType: ugcSummary?.travelTypes?.[0],
        nextOTA: ota,
        start: offset,
        limit,
        subconcepts: subConcepts,
        sortCriteria: sortBy,
      };
      try {
        setIsLoading(true);
        const response = await fetchUgcReviews({requestData, client: flavour, pokus: pokusExpData});
        const responseData = response?.data?.response || {};
        const reviewsCount = responseData?.reviews?.reviewList?.length || 0;

        setNextOta(responseData?.nextOTA);
        setReviews(responseData?.reviews?.reviewList || []);
        if (responseData?.reviews?.ota === 'GI') {
          setGiReviewsCount(responseData?.giReviewCount ?? giReviewsCount);
        }
        if (totalReviewsCount.current === 0) {
          totalReviewsCount.current =
            (responseData?.giReviewCount || 0) + (responseData?.expReviewCount || 0);
        }
        if (responseData?.nextOTA !== nextOta && reviewsCount < limit) {
          const remainingReviews = limit - reviewsCount;
          const nextReviews = await fetchReviews({
            offset: 0,
            limit: remainingReviews,
            ota: responseData?.nextOTA,
          });
          setReviews([...responseData.reviews.reviewList, ...nextReviews]);
        }
        if (responseData?.reviews?.ota === 'EXP') {
          setIsExpediaReviewLoaded(true);
        }
        return responseData.reviews.reviewList;
      } catch (error) {
        setError(error);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [ugcSummary, hotelId, countryCode, subConcepts, sortBy, flavour, nextOta],
  );

  const updateReviewData = useCallback(
    ({offset = 0, limit = TOTAL_REVIEWS_PER_PAGE}) => {
      const ota = offset <= giReviewsCount ? initialOTA : nextOta;
      if (offset === 0) {
        setNextOta(initialOTA);
      }
      if (giReviewsCount < offset) {
        const newOffset = offset - giReviewsCount;
        fetchReviews({offset: newOffset, limit, ota});
      } else {
        fetchReviews({offset, limit, ota});
      }
    },
    [fetchReviews, nextOta],
  );

  const onChangePage = useCallback(
    ({offset, limit}) => {
      setCurrentOffset(offset);
      updateReviewData({offset, limit});

      if (isInitializedRef.current && scrollId) {
        scrollToEl(scrollId, {behavior: isMobile ? 'auto' : 'smooth'});
      }
    },
    [scrollId, updateReviewData],
  );

  useEffect(() => {
    if (!isInitializedRef.current) return;
    onChangePage({offset: 0, limit: TOTAL_REVIEWS_PER_PAGE});
  }, [subConcepts, sortBy]);

  useEffect(() => {
    totalReviewsCount.current = (giReviewCount || 0) + (expReviewCount || 0);
    if (reviewList?.length === 0) {
      onChangePage({offset: 0, limit: TOTAL_REVIEWS_PER_PAGE});
    }
    isInitializedRef.current = true;
  }, []);

  useEffect(() => {
    if (isExpediaReviewLoaded) {
      onExpediaReviewsLoaded();
    }
  }, [isExpediaReviewLoaded, onExpediaReviewsLoaded]);

  return {
    totalReviewsCount: totalReviewsCount.current,
    onChangePage,
    reviews,
    isLoading,
    error,
    page,
  };
};

export default useFetchGiOtaReviewsV2;
